import React, { useCallback, useMemo, useRef } from 'react';
import classes from './DetailedStatsModal.module.scss';
import { Modal } from '../../../../components/containers/Modal';
import { PostItemFragment, PostStatus } from '../../../../models/graphql';
import { CardOrientationEnum } from '../../../../components/cards/BasePostCard/BasePostCard.view';
import { PostQueueStat } from '../../../../components/containers/PostQueueStat';
import { PostQueueStatLabelsEnum } from '../../../../components/containers/PostQueueStat/PostQueueStat.view';
import { ContainerListItem } from '../../../../components/actions/ListItem/ListItem.view';
import { Loader } from '../../../../components/containers/Loader';
import { Tooltip } from '../../../../components/actions/Tooltip';
import { DuplicateOrReschedulePostHandler, PostScheduleViewContext } from '../PostSchedule.controller';
import { BasePostCard } from '../../../../components/cards/BasePostCard';
import scssVars from '../../../../assets/scss/exportedVars.module.scss';
import { useDimensions } from '../../../../hooks/useDimensions';
import { BiggerScreenPlaceholder } from '../../../../components/containers/BiggerScreenPlaceHolder';
import { BreakpointsValuesEnum } from '../../../../constants/breakpoints-values';
import { PostCardStatisticsTable } from '../../../../components/cards/PostCardStatisticsTable';

export type DetailedStatsModalViewProps = {
  posts: PostItemFragment[];
  onClose: () => void;
  getActionMenuListItems: (post: PostItemFragment) => ContainerListItem[];
  reschedulePostHandler: DuplicateOrReschedulePostHandler;
};

export const DetailedStatsModalView = ({
  posts,
  onClose,
  getActionMenuListItems,
  reschedulePostHandler
}: DetailedStatsModalViewProps): JSX.Element => {
  const hideLabel = false;
  const stats = useMemo(() => {
    return posts[0]?.iterationAggregations;
  }, [posts]);

  const bodyRef = useRef<HTMLElement>(document.body);
  const { width } = useDimensions(bodyRef);

  // Detail modal Post Cards shoudl not have Statistics menu option
  const detailedPostCardMenu = useCallback(
    (post: PostItemFragment) => {
      const menuItems = getActionMenuListItems(post);
      return menuItems.filter(item => item.label !== 'Detailed Stats');
    },
    [getActionMenuListItems]
  );

  const boost = stats?.boost || 0;
  const roundedBoost = Math.round(boost);

  const postedPosts = useMemo(() => posts.filter(p => p.status === PostStatus.PUBLISHED), [posts]);

  return (
    <Modal onClose={onClose} showCloseIcon={true} title={'Post Stats Per Cycle'} size={'large'}>
      {width < BreakpointsValuesEnum.medium ? (
        <BiggerScreenPlaceholder />
      ) : (
        <div className={classes.DetailedStatsModal}>
          <div className={classes.AggregatedStatsContainer}>
            <div className={'grid-x'}>
              <div className={'cell auto'}>
                <h4>Aggregated Stats</h4>
              </div>
              <div className={'cell shrink'}>
                <div className={classes.BoostStatWrapper}>
                  <div className={classes.BoostStat}>
                    <PostQueueStat
                      label={PostQueueStatLabelsEnum.boost}
                      showBarGraph={false}
                      value={roundedBoost}
                      hideLabel={hideLabel}
                    />
                  </div>
                  <div className={classes.BoostTooltipWrapper}>
                    <Tooltip
                      type={'inlined'}
                      target={`#boost-stat`}
                      pointer={true}
                      position={'bottom'}
                      autoZIndex={false}
                      style={{ zIndex: scssVars['z-index-overmodal'] }}>
                      The SmarterQueue Boost is the total increase in engagement on an Evergreen post compared to if you
                      had only shared it once.
                    </Tooltip>
                  </div>
                </div>
              </div>
            </div>
            {/* make sure we have a post to reference first  */}
            {posts[0] && (
              <PostCardStatisticsTable
                post={posts[0]}
                context={PostScheduleViewContext.LIST}
                orientation={CardOrientationEnum.LANDSCAPE}
                isAggratedTable={true}
              />
            )}
          </div>
          {posts.length === 0 ? (
            <Loader variation={'dark'} size={'large'} />
          ) : postedPosts.length === 0 ? (
            <div className={classes.NoPublishedPosts}>There are no previously published posts</div>
          ) : (
            <>
              {[...postedPosts]
                .sort((a, b) => {
                  const dateA = new Date(a.datePosted || '').getTime();
                  const dateB = new Date(b.datePosted || '').getTime();

                  // Newest to Oldest
                  return dateB - dateA;
                })
                .map((post, index) => {
                  return (
                    <div className={'margin-bottom-small-8'} key={`detailed-card-${post.id}-${index}`}>
                      <BasePostCard
                        index={index}
                        context={PostScheduleViewContext.LIST}
                        orientation={CardOrientationEnum.LANDSCAPE}
                        post={post}
                        hideCheckbox={true}
                        menuListItems={detailedPostCardMenu(post)}
                        reschedulePostHandler={reschedulePostHandler}
                        isDetailedPastPost={true}
                        isDraggable={false}
                        showBarGraph={true}
                        moveCard={undefined}
                        useFormInstance={undefined}
                      />
                    </div>
                  );
                })}
            </>
          )}
          <div className={classes.FooterPaddingBorder} />
        </div>
      )}
    </Modal>
  );
};
